import { useState, useEffect } from 'react';
import { C } from '../../lib/tokens.js';

// Board-style filter pill: a button that opens a dropdown of options, with an
// optional search box. Two modes:
//  - sync (default): `options` is the full, already-loaded list; typing in the
//    search box filters it client-side (e.g. the Request Board's bpartner filter).
//  - async: pass `onSearch(query)` (returns a Promise<options>) to fetch the
//    list instead - called once when the dropdown opens (query='') and again,
//    debounced, on every keystroke. Since the picked option may not be part of
//    whatever page of results is currently loaded, pass `valueLabel` so the
//    pill can still show the right label after closing.
// searchPlaceholder/noMatchesLabel/loadingLabel are plain strings, not run
// through t() in here - this component is also consumed from other packages
// (e.g. the afspafts extension) via the cross-bundle import map, where the
// host's I18nProvider Context never bridges (separate Vite build, separate
// module instance -> useContext() always sees the default value). A caller
// that DOES have working i18n (the Request Board) passes its own t(...)
// results in; everyone else gets a sane English default.
export function FilterPill({
  label, value, options, onChange, icon, searchable = false, fullWidth = false,
  onSearch, valueLabel, loadingLabel = 'Loading…',
  searchPlaceholder = 'Type to search…', noMatchesLabel = 'No matches',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  function close() {
    setOpen(false);
    setQuery('');
  }

  useEffect(() => {
    if (!onSearch || !open) return undefined;
    let cancelled = false;
    setLoading(true);
    const trimmed = query.trim();
    const handle = setTimeout(() => {
      onSearch(trimmed)
        .then(opts => { if (!cancelled) setAsyncOptions(opts || []); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, trimmed ? 300 : 0);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [onSearch, open, query]);

  const found = options.find(o => (o.id || '') === (value || ''));
  const cur = found || (value && valueLabel ? { id: value, name: valueLabel } : options[0]);
  const isActive = !!value;

  const filteredOptions = onSearch
    ? asyncOptions
    : (searchable && query.trim()
      ? options.filter(o => (o.name ?? o.label ?? '').toLowerCase().includes(query.trim().toLowerCase()))
      : options);

  return (
    <div style={{ position: 'relative', flexShrink: 0, width: fullWidth ? '100%' : undefined }}>
      <button type="button" onClick={() => (open ? close() : setOpen(true))}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: fullWidth ? '10px 12px' : '6px 10px', borderRadius: 4,
          whiteSpace: 'nowrap', width: fullWidth ? '100%' : undefined, boxSizing: 'border-box',
          border: `1px solid ${isActive ? C.liquid : C.mercury}`,
          background: isActive ? C.rowActive : C.tin, color: isActive ? C.deepsea : C.fg,
          fontSize: 12, fontFamily: 'Roboto', cursor: 'pointer', fontWeight: 500,
        }}>
        {icon ? <span className="material-icons" style={{ fontSize: fullWidth ? 16 : 14, color: isActive ? C.liquid : C.iron }}>{icon}</span> : null}
        <span style={{ color: C.iron, fontWeight: 400 }}>{label}:</span>
        {fullWidth && <span style={{ flex: 1 }} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cur?.name ?? cur?.label}</span>
        <span className="material-icons" style={{ fontSize: 14, color: C.iron }}>expand_more</span>
      </button>
      {open ? (
        <>
          {!fullWidth && <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />}
          <div style={fullWidth ? {
            marginTop: 6, background: C.platinum, border: `1px solid ${C.mercury}`, borderRadius: 4, padding: 4,
          } : {
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
            background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 4,
            boxShadow: '0 6px 16px rgba(34,52,105,0.14)', minWidth: 200,
            maxWidth: 'calc(100vw - 40px)', padding: 4,
          }}>
            {searchable || onSearch ? (
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
                placeholder={searchPlaceholder}
                style={{
                  width: '100%', boxSizing: 'border-box', marginBottom: 4, padding: '6px 8px',
                  border: `1px solid ${C.mercury}`, borderRadius: 3, fontSize: 12, fontFamily: 'Roboto', outline: 'none',
                }}
              />
            ) : null}
            <div style={(searchable || onSearch) ? { maxHeight: 240, overflowY: 'auto' } : undefined}>
              {loading ? (
                <div style={{ padding: '7px 10px', fontSize: 12, color: C.iron, fontStyle: 'italic' }}>{loadingLabel}</div>
              ) : filteredOptions.length === 0 ? (
                <div style={{ padding: '7px 10px', fontSize: 12, color: C.iron, fontStyle: 'italic' }}>{noMatchesLabel}</div>
              ) : filteredOptions.map(o => {
                const active = (o.id || '') === (value || '');
                return (
                  <button key={o.id || '__none'} type="button"
                    onClick={() => { onChange(o.id || '', o); close(); }}
                    style={{
                      width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none',
                      background: active ? C.rowActive : 'transparent',
                      color: active ? C.deepsea : C.fg, fontSize: 12, cursor: 'pointer', borderRadius: 3,
                      display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Roboto',
                    }}>
                    {o.color ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.color }} /> : null}
                    {o.icon && !o.color ? <span className="material-icons" style={{ fontSize: 14, color: C.iron }}>{o.icon}</span> : null}
                    {o.name ?? o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
